import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProductDetailsData, ProductGridData } from '../types/types';


export type LastSearchParams = {
  searchValue: string;
  selectedField: string;
  orgId: number;
  userId: number;
} | null;

export type ProductState = {
  products: ProductGridData[];
  selectedProduct: ProductDetailsData | null;
  error: string;
  loading: boolean;
  selectedProductLoading: boolean;
  selectedRowId: string | null;
  lastSearchParams: LastSearchParams;
};

// Define clean initial state - redux-persist will handle loading
const initialState: ProductState = {
  products: [],
  selectedProduct: null,
  error: '',
  loading: false,
  selectedProductLoading: false,
  selectedRowId: null,
  lastSearchParams: null,
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProducts: (state, action) => {
      state.products = action.payload;
    },
    addProduct: (state, action) => {
      state.products.push(action.payload);
    },
    updateProduct: (state, action) => {
      const index = state.products.findIndex((p: ProductGridData) => p.rowId === action.payload.rowId);
      if (index !== -1) {
        state.products[index] = action.payload;
      }
    },
    deleteProduct: (state, action) => {
      state.products = state.products.filter((p: ProductGridData) => p.rowId !== action.payload);
    },
    setProductError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setProductLoading: (state, action) => {
      state.loading = action.payload;
    },

    resetProductState: (state) => {
      state.products = [];
      state.error = '';
      state.loading = false;
    },
    setSelectedProduct: (state, action: PayloadAction<ProductDetailsData>) => {
      state.selectedProduct = action.payload;
      state.selectedProductLoading = false;
    },
    setSelectedProductLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedProductLoading = action.payload;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
      state.selectedProductLoading = false;
      state.selectedRowId = null;
    },
    setSelectedRowId: (state, action: PayloadAction<string | null>) => {
      state.selectedRowId = action.payload;
    },
    setLastSearchParams: (state, action: PayloadAction<LastSearchParams>) => {
      state.lastSearchParams = action.payload;
    },
    clearLastSearchParams: (state) => {
      state.lastSearchParams = null;
    },
  },
});

export const {
  setProducts,
  addProduct,
  updateProduct,
  deleteProduct,
  setProductError,
  setProductLoading,
  resetProductState,
  setSelectedProduct,
  setSelectedProductLoading,
  clearSelectedProduct,
  setSelectedRowId,
  setLastSearchParams,
  clearLastSearchParams,
} = productSlice.actions;

export const productReducer = productSlice.reducer;
